import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';

import {
  ColumnFiltersState,
  useReactTable,
  getSortedRowModel,
  getCoreRowModel,
  HeaderGroup,
  Row,
  Table,
  getFilteredRowModel,
  getFacetedMinMaxValues,
} from '@tanstack/react-table';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { TApplication } from 'lendingApi/applications.types';
import { useDashboard } from '../Dashboard.context';
import {
  COLUMN_IDS,
  getDefaultColumns,
} from './ApplicationsTableState.context.helpers';

const ApplicationsTableStateContext = createContext<
  | {
      headerGroups: HeaderGroup<TApplication>[];
      rows: Row<TApplication>[];
      columnOrder: string[];
      table: Table<TApplication>;
      dateRange: string;
      includePartnerChannel: boolean;
      setColumnOrder: Dispatch<SetStateAction<string[]>>;
      setDateRange: (dateRange: string) => void;
      setIncludePartnerChannel: (prevValue: boolean) => void;
    }
  | undefined
>(undefined);

export const ApplicationsTableStateProvider = (props: any) => {
  const { state: applications } = useDashboard();
  const { introducer, introducerCompany } = useIntroducerContext();
  const showReferredByColumn =
    introducer?.master_broker || introducerCompany?.shared_applications;
  const data: TApplication[] = applications.applications ?? [];

  const defaultSettings = {
    columnOrder: [
      COLUMN_IDS.created,
      COLUMN_IDS.companyName,
      COLUMN_IDS.applicant,
      COLUMN_IDS.status,
      COLUMN_IDS.termLength,
      COLUMN_IDS.approvalAmount,
      COLUMN_IDS.requestedAmount,
      COLUMN_IDS.expiryDate,
      COLUMN_IDS.referredBy,
    ],
    columnVisibility: {},
    dateRange: '1 month',
    includePartnerChannel: false,
  };

  const storedSettings = JSON.parse(
    localStorage.getItem('applicationsTableSettings') || 'null',
  );

  const [columnOrder, setColumnOrder] = useState<string[]>(
    storedSettings?.columnOrder || defaultSettings.columnOrder,
  );

  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(storedSettings?.columnVisibility || defaultSettings.columnVisibility);

  const [dateRange, setDateRange] = useState<string>(
    storedSettings?.dateRange || defaultSettings.dateRange,
  );
  const [includePartnerChannel, setIncludePartnerChannel] = useState(
    storedSettings?.includePartnerChannel ||
      defaultSettings.includePartnerChannel,
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  useEffect(() => {
    localStorage.setItem(
      'applicationsTableSettings',
      JSON.stringify({
        columnOrder,
        columnVisibility,
        dateRange,
        includePartnerChannel,
      }),
    );
  }, [columnOrder, columnVisibility, dateRange, includePartnerChannel]);

  const table = useReactTable({
    data: data,
    columns: getDefaultColumns(showReferredByColumn),
    state: {
      columnOrder,
      columnVisibility,
      columnFilters,
    },
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });

  const headerGroups = table.getHeaderGroups();
  const rows = table.getRowModel().rows;

  return (
    <ApplicationsTableStateContext.Provider
      value={{
        table,
        headerGroups,
        rows,
        dateRange,
        includePartnerChannel,
        columnOrder,
        setColumnOrder,
        setDateRange,
        setIncludePartnerChannel,
      }}
      {...props}
    />
  );
};

export const useApplicationsTableState = () => {
  const context = useContext(ApplicationsTableStateContext);
  if (context === undefined) {
    throw new Error('useApplicationsTableState must be used within');
  }
  return context;
};
